import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import { isSafeImageSrc } from "../../services/safe-image-src";
import type { LineupColumn, LineupSubscriptionRow } from "./channel-lineup.types";
import { Indicator, TooltipTrigger } from "./indicator";
import { indicatorAccessibleName } from "./indicator.shared";
import type { LineupRowCopy } from "./row-copy.shared";
import styles from "./subscription-row.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One subscription row of the Signature packages grid — GR6. The logo on the left with its name as
 * a tooltip, a ✓ under every package that includes it, and the device-lease note as a strip across
 * the package cells underneath.
 */
export interface SubscriptionRowProps {
  /** RESOLVER-FED (camelCase). The subscription and its positional cells. */
  row: LineupSubscriptionRow;
  /** RESOLVER-FED (camelCase). The grid's columns, in authored order. Read
   *  for each cell's package label (AX3) and for the track count. */
  columns: LineupColumn[];
  /** The authored cell names and tooltip prefix (`row-copy.shared.ts`). */
  rowCopy?: LineupRowCopy;
  /** Layout only. */
  className?: string;
}

/**
 * GR6's logo tile — the name behind, the image over it. Split out because it is a two-armed branch
 * (named and unnamed) inside a row that is otherwise a loop, the same shape `logo-grid.tsx` splits
 * its own tile into.
 */
function SubscriptionLogo({ name, logoUrl }: { name: string; logoUrl?: string }) {
  const safeLogo = isSafeImageSrc(logoUrl) ? logoUrl : "";

  const tile = (
    <span className={styles.logo}>
      {/* The NO-LOGO case (GR6, GP5), and what sizes the tile wide enough for
          the alt text below to lay out in. Covered by the image when there is
          one — see the stylesheet. */}
      <Text as="span" className={styles.logoName} variant="label">
        {name}
      </Text>
      {safeLogo !== "" && (
        /* `alt={name}`, not `alt=""` — this is GR6's "if a subscription logo fails to load, its
           name shows instead", and the alt text is the only mechanism that delivers it without a
           client error handler. */
        <img
          alt={name}
          className={styles.logoImage}
          decoding="async"
          loading="lazy"
          src={safeLogo}
        />
      )}
    </span>
  );

  if (!name) {
    return tile;
  }

  return (
    <TooltipTrigger content={name} label={name}>
      {tile}
    </TooltipTrigger>
  );
}

function SubscriptionRowImpl({ row, columns, rowCopy, className }: SubscriptionRowProps) {
  const { subscription } = row;
  /* GR9 — names and labels always show as plain text, never as formatting or
     as visible tags. The same reduction every other label in this grid gets. */
  const name = plainText(subscription.name);
  const note = plainText(subscription.deviceLeaseNote ?? "");

  return (
    <div className={[styles.row, className].filter(Boolean).join(" ")}>
      {/* The span is conditional on the NOTE, and the stylesheet records why: a `grid-row` span is only reachable when the row it reaches for exists, and the note strip below is what creates it. */}
      <span
        className={[styles.identityCell, note !== "" ? styles.identityCellSpanning : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <SubscriptionLogo logoUrl={subscription.logoUrl} name={name} />
      </span>

      {columns.map((column, index) => (
        <div className={styles.cell} key={column.billingProductCode}>
          {row.cells[index] === true ?
            (
              <Indicator
                channelName={name}
                membership="included"
                packageLabel={column.label}
                stateLabels={rowCopy?.stateLabels}
                tooltipPrefix={rowCopy?.tooltipPrefixes?.included}
              />
            ) :
            /* AX3 — the empty cell's name; nothing is drawn here to carry it. */
            <span className="dds-sr-only">
              {indicatorAccessibleName(name, column.label, null, rowCopy?.stateLabels)}
            </span>}
        </div>
      ))}

      {note !== "" && (
        <Text as="p" className={styles.note} variant="body-3">
          {note}
        </Text>
      )}
    </div>
  );
}
export const SubscriptionRow = withSafeRender(SubscriptionRowImpl, "SubscriptionRow");

export default SubscriptionRow;
